import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { object, string, ZodError } from 'zod';
import AuthLayout from '../../components/AuthLayout';
import { useToast } from '../../components/common/ToasterContext';
import SignInComponent from '../../components/SignIn';
import { Credentials } from '../../components/SignIn/types';
import getAPIUrl from '../../config';
import postApi from '../../utils/apiCalls';
import { getAccessToken, setLocalStorageData } from '../../utils/token';
import { trimFormData } from '../../utils/helpers';

const SignIn = () => {
    const [formErrors, setFormErrors] = useState<{
        [key: string]: Array<string>;
    }>();
    const toast = useToast();
    const signInValidationSchema = object({
        email: string().email({ message: 'Please provide a valid email address.' }),
        password: string().min(8, 'Password length must be 8 characters.'),
    });

    const [credentials, setCredentials] = useState<Credentials>({
        email: '',
        password: '',
    });
    const [submitting, setSubmitting] = useState<boolean>(false);

    const navigate = useNavigate();

    useEffect(() => {
        const isToken = getAccessToken();
        if (isToken) {
            navigate('/dashboard');
        }
    }, []);

    const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        try {
            setSubmitting(true);
            signInValidationSchema.parse(trimFormData(credentials));
            const res = await postApi(`${getAPIUrl()}/admins/login`, credentials);
            setLocalStorageData(res.data.data);
            setFormErrors(undefined);
            navigate('/dashboard');
        } catch (error: any) {
            if (error instanceof ZodError) {
                setFormErrors(error.flatten().fieldErrors);
            } else if (toast) {
                setFormErrors({ beError: [`${error.response.data.errors}`] });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const { value, name } = e.currentTarget;
        setCredentials({
            ...credentials,
            [name]: value,
        });
    };

    return (
        <AuthLayout
            component={
                <SignInComponent
                    handleSubmit={onSubmit}
                    formErrors={formErrors}
                    handleInput={handleInput}
                    credentials={credentials}
                    submitting={submitting}
                />
            }
        />
    );
};
export default SignIn;
